import { useState, useEffect, useCallback } from "react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail } from "lucide-react";

interface OtpStepProps {
  email: string;
  onSubmit: (otp: string) => void;
  onResend: () => void;
  onBack: () => void;
  loading: boolean;
}

export function OtpStep({ email, onSubmit, onResend, onBack, loading }: OtpStepProps) {
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleChange = useCallback(
    (value: string) => {
      setOtp(value);
      if (value.length === 6) {
        onSubmit(value);
      }
    },
    [onSubmit]
  );

  const handleResend = () => {
    onResend();
    setCooldown(60);
    setOtp("");
  };

  return (
    <div className="space-y-5 rounded-2xl border bg-card p-5 shadow-card sm:p-8">
      <div className="space-y-3 text-center">
        <p className="text-sm text-muted-foreground">
          Enviamos um código de 6 dígitos para
        </p>
        <p className="mx-auto inline-flex max-w-full items-center gap-2 rounded-xl bg-secondary px-3 py-2 text-sm font-semibold">
          <Mail className="h-4 w-4 shrink-0 text-primary" />
          <span className="truncate">{email}</span>
        </p>
      </div>

      <div className="flex justify-center">
        <InputOTP
          maxLength={6}
          value={otp}
          onChange={handleChange}
          disabled={loading}
          autoFocus
          autoComplete="one-time-code"
          inputMode="numeric"
        >
          <InputOTPGroup className="gap-1.5 sm:gap-2">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <InputOTPSlot key={index} index={index} className="h-12 w-10 rounded-lg border-l text-lg sm:w-11" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      {loading && (
        <p className="text-center text-sm text-muted-foreground">
          Verificando...
        </p>
      )}

      <div className="flex flex-col items-center gap-2 text-sm">
        {cooldown > 0 ? (
          <p className="text-muted-foreground">
            Reenviar código em {cooldown}s
          </p>
        ) : (
          <Button variant="ghost" size="sm" onClick={handleResend}>
            Reenviar código
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onBack} className="text-primary hover:text-primary">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Trocar e-mail
        </Button>
      </div>
    </div>
  );
}
